import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, Gem, Compass, Wallet, Star, MapPin } from "lucide-react";
import { COUNTRIES, GALLERY, DOMESTIC, TESTIMONIALS, VIBES, formatINR } from "../mock";
import api, { imgUrl } from "../lib/api";
import TourCard from "../components/TourCard";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const features = [
  { icon: Gem, title: "Curated Quality", text: "Only trips we’d take ourselves—handcrafted experiences with no filler." },
  { icon: Compass, title: "Effortless Planning", text: "Clear itineraries, simple digital bookings, and responsive live support." },
  { icon: Wallet, title: "Honest Pricing", text: "Upfront costs and complete transparency. No surprises at checkout." },
];

export default function Home() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [where, setWhere] = useState("");
  const [tours, setTours] = useState([]);

  useEffect(() => {
    api.get("/tours").then((res) => setTours(res.data)).catch(() => {});
  }, []);

  const search = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (where) params.set("location", where);
    navigate(`/tours?${params.toString()}`);
  };

  const destinations = ["Indonesia", "Singapore", "Sri Lanka", "Vietnam", "Thailand"];
  const featuredImg = tours[6]?.gallery?.[1] || tours[0]?.cover;
  const ctaImg = tours[3]?.gallery?.[0] || tours[0]?.cover;

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="hero-dark relative pt-32 pb-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-5">
          <div className="max-w-3xl fade-up">
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-wide text-slate-300 border border-white/15 rounded-full px-3 py-1.5">
              <span className="text-amber-400">FLYCHER TRAVELS</span> • CURATED GETAWAYS
            </span>
            <h1 className="mt-6 text-5xl md:text-6xl font-bold text-white leading-[1.05]">
              Effortless <span className="gradient-text">Journeys.</span><br />
              Extraordinary <span className="gradient-text">Memories.</span>
            </h1>
            <p className="mt-5 text-lg text-slate-300 max-w-xl">
              Hand-picked group tours, crafted itineraries, and honest pricing. Plan less, live more.
            </p>

            <div className="mt-8 bg-white rounded-2xl p-2 flex flex-col sm:flex-row gap-2 shadow-2xl">
              <div className="flex items-center gap-2 flex-1 px-3">
                <Search className="w-5 h-5 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
                  placeholder="Search tours, themes..." className="border-0 shadow-none focus-visible:ring-0 px-0" />
              </div>
              <div className="flex items-center gap-2 sm:w-48 px-3 sm:border-l border-slate-200">
                <MapPin className="w-5 h-5 text-slate-400" />
                <Input value={where} onChange={(e) => setWhere(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
                  placeholder="Where to?" className="border-0 shadow-none focus-visible:ring-0 px-0" />
              </div>
              <Button onClick={search} className="bg-coral-600 hover:bg-coral-700 text-white rounded-xl px-6 h-11">
                <Search className="w-4 h-4 mr-2" /> Find Trips
              </Button>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <span className="text-sm text-slate-400">Popular destinations:</span>
              {destinations.map((d) => (
                <button key={d} onClick={() => navigate(`/tours?location=${encodeURIComponent(d)}`)}
                  className="text-sm text-white/90 bg-white/10 hover:bg-white/20 border border-white/10 rounded-full px-3 py-1 transition-colors">
                  {d}
                </button>
              ))}
            </div>
          </div>

          {/* Featured banner (video) */}
          <Link to={`/tour/${tours[0]?.slug || ""}`} className="mt-12 block relative rounded-3xl overflow-hidden group border border-white/10">
            <video
              className="w-full h-64 md:h-80 object-cover"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              poster={featuredImg ? imgUrl(featuredImg) : undefined}
            >
              <source src={`${process.env.PUBLIC_URL}/featured.webm`} type="video/webm" />
              <source src={`${process.env.PUBLIC_URL}/featured.mp4`} type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
            <div className="absolute bottom-6 left-6">
              <Button className="bg-coral-600 hover:bg-coral-700 text-white rounded-full">
                Explore Featured Package <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Link>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-5 -mt-10 relative z-10 grid gap-5 md:grid-cols-3">
        {features.map((f) => (
          <div key={f.title} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm card-hover">
            <span className="grid place-items-center w-12 h-12 rounded-xl bg-coral-50 text-coral-600 mb-4">
              <f.icon className="w-6 h-6" />
            </span>
            <h3 className="font-display font-semibold text-lg text-slate-900">{f.title}</h3>
            <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{f.text}</p>
          </div>
        ))}
      </section>

      {/* POPULAR GETAWAYS */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs font-semibold tracking-widest text-coral-600 uppercase">Featured Packages</p>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold text-slate-900">Popular Getaways</h2>
          </div>
          <Link to="/tours" className="text-sm font-semibold text-coral-600 hover:underline whitespace-nowrap">View all tours →</Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {tours.slice(0, 4).map((t) => <TourCard key={t.id} tour={t} />)}
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 mt-6">
          {tours.slice(4, 7).map((t) => <TourCard key={t.id} tour={t} />)}
        </div>
      </section>

      {/* TOP COUNTRIES */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-5">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs font-semibold tracking-widest text-coral-600 uppercase">Destinations</p>
              <h2 className="mt-2 text-3xl md:text-4xl font-bold text-slate-900">Top Countries</h2>
            </div>
            <Link to="/tours" className="text-sm font-semibold text-coral-600 hover:underline whitespace-nowrap">Browse all →</Link>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {COUNTRIES.map((c) => (
              <Link key={c.id} to={`/tours?location=${encodeURIComponent(c.name)}`} className="group relative rounded-2xl overflow-hidden card-hover">
                <img src={c.image} alt={c.name} className="w-full h-60 object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
                <span className="absolute top-4 left-4 text-xs font-semibold bg-black/50 text-white px-2.5 py-1 rounded-full">
                  {c.packages} Package{c.packages > 1 ? "s" : ""}
                </span>
                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                  <span className="font-display font-bold text-white text-xl">{c.name}</span>
                  <span className="text-right">
                    <span className="block text-[11px] text-white/70">Starting from</span>
                    <span className="font-display font-bold text-white">{formatINR(c.from)}</span>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* DOMESTIC */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs font-semibold tracking-widest text-coral-600 uppercase">Explore India</p>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold text-slate-900">Domestic Tours</h2>
          </div>
        </div>
        <div className="grid gap-6 sm:grid-cols-2">
          {DOMESTIC.map((d) => (
            <div key={d.title} className="group relative rounded-2xl overflow-hidden border border-slate-200 card-hover">
              <img src={d.image} alt={d.title} className="w-full h-56 object-cover transition-transform duration-700 group-hover:scale-110" />
              <span className="absolute bottom-4 right-4 font-display font-bold text-white bg-coral-600 px-3 py-1 rounded-full text-sm">{formatINR(d.price)}</span>
              <div className="p-4">
                <h3 className="font-display font-semibold text-slate-900">{d.title}</h3>
                <p className="text-sm text-slate-500">{d.note}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* GALLERY */}
      <section className="section-dark py-20">
        <div className="max-w-7xl mx-auto px-5">
          <p className="text-xs font-semibold tracking-widest text-amber-400 uppercase">Visual Stories</p>
          <h2 className="mt-2 text-3xl md:text-4xl font-bold text-white">Tour Highlights &amp; Gallery</h2>
          <div className="mt-8 grid gap-4 grid-cols-2 md:grid-cols-4">
            {GALLERY.slice(0, 8).map((g, i) => (
              <div key={i} className="group relative rounded-xl overflow-hidden">
                <img src={g.image} alt={g.tag} className="w-full h-44 object-cover transition-transform duration-700 group-hover:scale-110" />
                <span className="absolute top-2 left-2 text-[11px] font-semibold bg-black/50 text-white px-2 py-0.5 rounded">Tour</span>
                <span className="absolute bottom-2 left-2 text-sm font-semibold text-white">{g.tag}</span>
              </div>
            ))}
          </div>
          <div className="mt-10 rounded-2xl border border-white/10 p-6">
            <h3 className="font-display font-semibold text-white mb-4">What’s Your Travel Vibe?</h3>
            <div className="flex flex-wrap gap-3">
              {VIBES.map((v) => (
                <button key={v.q} onClick={() => navigate(`/tours?q=${v.q}`)} className="text-sm text-white bg-white/10 hover:bg-coral-600 border border-white/10 rounded-full px-4 py-2 transition-colors">
                  {v.emoji} {v.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <p className="text-xs font-semibold tracking-widest text-coral-600 uppercase">Testimonials</p>
        <h2 className="mt-2 text-3xl md:text-4xl font-bold text-slate-900">Loved by Travelers</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="bg-white rounded-2xl border border-slate-200 p-6 card-hover">
              <div className="flex gap-0.5 text-yellow-400 mb-3">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-yellow-400" />)}
              </div>
              <p className="text-slate-700 leading-relaxed">“{t.text}”</p>
              <p className="mt-4 text-sm font-semibold text-slate-900">— {t.name} • <span className="text-slate-500 font-normal">{t.trip}</span></p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative">
        {ctaImg && <img src={imgUrl(ctaImg)} alt="Adventure" className="w-full h-80 object-cover" />}
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="absolute inset-0 grid place-items-center text-center px-5">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Ready to start your next adventure?</h2>
            <p className="mt-3 text-slate-200">Tell us where you want to go, and we’ll sort out everything else.</p>
            <Link to="/tours"><Button className="mt-6 bg-coral-600 hover:bg-coral-700 text-white rounded-full px-6 h-12">Explore All Tours <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
